import React, { Fragment, useEffect, useState } from "react";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import TextField from "@mui/material/TextField";
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import ClearIcon from '@mui/icons-material/Clear';
import SearchIcon from '@mui/icons-material/Search';
import { TranscriptViewModel } from "./TranscriptViewModel";
import TranscriptTextFinder from "./TranscriptTextFinder";
import TranscriptSearch, { eSearchModes } from "./TranscriptSearch";

interface ITranscriptTextFinderBarProps {
    transcript: TranscriptViewModel;
    transcriptSearch?: TranscriptSearch;
    transcriptTextFinder: TranscriptTextFinder | undefined
    setTranscriptTextFinder: (transcriptTextFinder: TranscriptTextFinder | undefined) => void;
}

export default function TranscriptTextFinderBar(props: ITranscriptTextFinderBarProps) {
    const [transcriptTextFinderText, setTranscriptTextFinderText] = useState("");
    let selectedIndex = 0;

    useEffect(() => {
        if (props.transcriptSearch?.mode && props.transcript) {
            let searchText = props.transcriptSearch.searchText;

            if (props.transcriptSearch.mode === eSearchModes.Similar) {
                if (props.transcriptSearch.similarWords.length > 0) {
                    searchText = props.transcriptSearch.similarWords.join(', ');
                }
            }

            if (props.transcriptSearch.mode === eSearchModes.All || props.transcriptSearch.mode === eSearchModes.Any) {
                searchText = props.transcriptSearch.searchText.split(' ').join(', ');
            }

            setTranscriptTextFinderText(searchText);
            props.setTranscriptTextFinder({ ...new TranscriptTextFinder(searchText, props.transcript) });
        }
    }, [props.transcriptSearch, props.transcript])

    useEffect(() => {
        if (props.transcriptTextFinder) {
            if (props.transcriptTextFinder.matchCount > 0) {
                selectedIndex = 0;
                updateMatchCount(selectedIndex + 1);
                updateHightlights();
            }
            else {
                updateMatchCount(0);
            }
        }
    }, [props.transcriptTextFinder?.messages])

    const handleTranscriptTextFinderSubmit = () => {
        clearHighlights();
        props.setTranscriptTextFinder({ ... new TranscriptTextFinder(transcriptTextFinderText, props.transcript!) });
    }

    const clearHighlights = () => {
        const highlightedLines = document.getElementsByClassName('Text-Match');
        if (highlightedLines) {
            Array.from(highlightedLines).forEach(x => {
                let element = (x as HTMLElement);
                element.style.backgroundColor = 'transparent';
                for (let className in element.classList) {
                    if (className.startsWith('Text-Match')) {
                        element.classList.remove(className);
                    }
                }
                element.id = "";
            })
        };
    }

    const updateHightlights = () => {
        const selectedMatchElements = Array.from(document.getElementsByClassName(`Text-Match-${selectedIndex}`));
        if (selectedMatchElements) {
            selectedMatchElements[0]?.scrollIntoView({
                behavior: 'smooth',
                block: 'center',
                inline: 'nearest',
            });

            selectedMatchElements.forEach(selectedElement => (selectedElement as HTMLElement).style.backgroundColor = '#e9851c');
        }

        const highlightedLines = document.getElementsByClassName('Text-Match');
        if (highlightedLines) {
            Array.from(highlightedLines)
                .forEach(x =>
                    !selectedMatchElements.includes(x)
                        ? (x as HTMLElement).style.backgroundColor = 'yellow'
                        : undefined);
        }
    }

    const updateMatchCount = (newValue: number) => {
        var matchCount = document.getElementById('Match-Count');
        if (matchCount) {
            matchCount.innerHTML = `${newValue}/${props.transcriptTextFinder!.matchCount}`
        }
    }

    const incrementTextFindSelector = () => {
        if (props.transcriptTextFinder?.matchCount) {
            if (selectedIndex === props.transcriptTextFinder.matchCount - 1) {
                selectedIndex = 0;
            }
            else {
                selectedIndex++;
            }
            updateMatchCount(selectedIndex + 1);
            updateHightlights();
        }
    }

    const decrementTextFindSelector = () => {
        if (props.transcriptTextFinder?.matchCount) {
            if (selectedIndex === 0) {
                selectedIndex = props.transcriptTextFinder.matchCount - 1;
            }
            else {
                selectedIndex--;
            }
            updateMatchCount(selectedIndex + 1);
            updateHightlights();
        }
    }

    const clearTextFinder = () => {
        props.setTranscriptTextFinder(undefined);
        setTranscriptTextFinderText("");
        clearHighlights();
    }

    return (
        <Grid container justifyContent="flex-end" alignItems="center" size={12} sx={{ marginBottom: '12px', marginTop: '12px' }} >
            <Grid container border="solid 1px #1d8abf" borderRadius="5px" alignItems="center" flexWrap="nowrap" flexShrink={1} size="auto" marginRight={1}>
                <Grid flexGrow={1} flexShrink={1}>
                    <TextField
                        id="transcript-text-finder"
                        variant="outlined"
                        size="small"
                        onChange={(e: any) => setTranscriptTextFinderText(e.target.value)}
                        onKeyDown={(e: any) => {
                            if (e.key === 'Enter') {
                                handleTranscriptTextFinderSubmit();
                            }
                            else if (props.transcriptTextFinder) {
                                if (e.key === 'ArrowUp') {
                                    decrementTextFindSelector();
                                }
                                else if (e.key === 'ArrowDown') {
                                    incrementTextFindSelector();
                                }
                            }
                        }}
                        autoComplete="off"
                        value={transcriptTextFinderText}
                        sx={{ "& fieldset": { border: 'none', borderRight: 'solid 1px #1d8abf', borderRadius: 0, marginBottom: '1px' } }}
                        InputProps={props.transcriptTextFinder ? {
                            endAdornment: <Typography id={'Match-Count'} sx={{ paddingLeft: 2 }}>{
                                props.transcriptTextFinder?.matchCount
                                && `${props.transcriptTextFinder?.matchCount}`}
                            </Typography>
                        }
                            : {}}
                    />
                </Grid>
                {
                    props.transcriptTextFinder &&
                    <Fragment>
                        <Grid>
                            <Button onClick={decrementTextFindSelector} size="small" sx={{ minWidth: "42px" }} disabled={props.transcriptTextFinder.matchCount === 0}>
                                <KeyboardArrowUpIcon
                                    fontSize="medium"
                                    sx={props.transcriptTextFinder.matchCount === 0 ? { color: 'grey' } : { color: '#1d8abf' }} />
                            </Button>
                        </Grid>
                        <Grid>
                            <Button onClick={incrementTextFindSelector} size="small" sx={{ minWidth: "42px" }} disabled={props.transcriptTextFinder.matchCount === 0}>
                                <KeyboardArrowDownIcon
                                    fontSize="medium"
                                    sx={props.transcriptTextFinder.matchCount === 0 ? { color: 'grey' } : { color: '#1d8abf' }} />
                            </Button>
                        </Grid>
                        <Grid>
                            <Button onClick={clearTextFinder} size="small" sx={{ minWidth: "42px" }} disabled={props.transcriptTextFinder.matchCount === 0}>
                                <ClearIcon
                                    fontSize="medium"
                                    sx={{ color: 'grey' }} />
                            </Button>
                        </Grid>
                    </Fragment>
                }
                <Grid>
                    <Button
                        onClick={handleTranscriptTextFinderSubmit} size="small" sx={{ minWidth: "42px" }}>
                        <SearchIcon fontSize="medium" sx={{ color: '#1d8abf' }} />
                    </Button>
                </Grid>
            </Grid>
        </Grid >
    );
}